import { apiClient } from './client';
import type { Paginated } from './types';
import type { FichePaie } from './payroll';
import type { CodeOffre, Repartition } from './finance';
import type { Facture } from './billing';

/**
 * Endpoints `/api/v1/contracts/` — Phase 3 (devis & contrats).
 *
 * Wrappers axios typés ; la couche réseau seulement, le cache est géré par
 * TanStack Query au point d'appel. Les serializers DRF exposent les clés
 * étrangères (`client`, `personnel`) sous forme d'identifiants ; la console
 * Admin résout les libellés via `listClients` / `listPersonnel`.
 */

// ── Énumérations métier ─────────────────────────────────────
export type DevisStatut = 'BROUILLON' | 'ENVOYE' | 'ACCEPTE' | 'REFUSE' | 'EXPIRE';

export const DEVIS_STATUT_LABELS: Record<DevisStatut, string> = {
  BROUILLON: 'Brouillon',
  ENVOYE: 'Envoyé',
  ACCEPTE: 'Accepté',
  REFUSE: 'Refusé',
  EXPIRE: 'Expiré',
};

export type ContractType = 'MENSUEL' | 'PONCTUEL';

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  MENSUEL: 'Mensuel',
  PONCTUEL: 'Ponctuel',
};

export type ContractStatut = 'EN_REVUE' | 'ACTIF' | 'SUSPENDU' | 'TERMINE' | 'RESILIE';

export const CONTRACT_STATUT_LABELS: Record<ContractStatut, string> = {
  EN_REVUE: 'En revue',
  ACTIF: 'Actif',
  SUSPENDU: 'Suspendu',
  TERMINE: 'Terminé',
  RESILIE: 'Résilié',
};

// ── Avenant d'affectation ───────────────────────────────────
export type AvenantType =
  | 'AFFECTATION_INITIALE'
  | 'REMPLACEMENT_DEFINITIF'
  | 'REMPLACEMENT_TEMPORAIRE';

export const AVENANT_TYPE_LABELS: Record<AvenantType, string> = {
  AFFECTATION_INITIALE: 'Affectation initiale',
  REMPLACEMENT_DEFINITIF: 'Remplacement définitif',
  REMPLACEMENT_TEMPORAIRE: 'Remplacement temporaire',
};

export type AvenantStatut = 'PLANIFIE' | 'ACTIF' | 'SUSPENDU' | 'CLOS' | 'EXPIRE';

export const AVENANT_STATUT_LABELS: Record<AvenantStatut, string> = {
  PLANIFIE: 'Planifié',
  ACTIF: 'Actif',
  SUSPENDU: 'Suspendu',
  CLOS: 'Clos',
  EXPIRE: 'Expiré',
};

/** Avenant d'affectation rattaché à un contrat-cadre. */
export interface Avenant {
  id: string;
  contract: string;
  contrat_travail: string;
  personnel: string;
  personnel_nom?: string;
  personnel_metier?: string;
  type: AvenantType;
  statut: AvenantStatut;
  ordre: number;
  motif: string;
  salaire_mensuel: string;
  /** Prix client de cette prestation (multi-prestations) ; null = mono-prestation. */
  prix_unitaire?: string | null;
  date_effet: string;
  date_fin: string | null;
  lieu: string;
  taches: string[] | null;
  horaires: Record<string, string> | null;
  avenant_remplace: string | null;
  signature_client_at: string | null;
  signature_personnel_at: string | null;
  /** Date de signature par l'employeur (Harmaury / Stéphanie MBOUSSI). Null si non encore signé. */
  signature_employeur_at: string | null;
  /** Vrai si signé des trois parties (client + personnel + employeur). */
  signe: boolean;
  created_at: string;
  updated_at: string;
}

// ── Sous-objets ─────────────────────────────────────────────
export interface LigneDevis {
  id: string;
  devis: string;
  libelle: string;
  quantite: number;
  prix_unitaire: string; // DecimalField sérialisé en chaîne par DRF
  montant: string;
  created_at: string;
}

/** Ligne d'option de service rattachée à un devis. */
export interface DevisOption {
  id: string;
  service: number;
  service_code: string;
  service_nom: string;
  service_categorie: string;
  quantite: number;
  /** Prix unitaire du service au moment de la sélection (XAF, chaîne). */
  prix: string;
  /** Montant total = prix * quantite (XAF, chaîne). */
  montant: string;
}

export interface Devis {
  id: string;
  client: string;
  personnel: string | null;
  type_service: string;
  statut: DevisStatut;
  montant_total: string; // DecimalField (XAF) — abonnement de base uniquement
  date_validite: string; // ISO yyyy-MM-dd
  /** Date de début souhaitée du service (yyyy-MM-dd), transmise au contrat à l'acceptation. */
  date_debut?: string | null;
  /** Tâches convenues transmises au contrat. */
  taches?: string[] | null;
  /** Horaires par jour (ex. { "lundi": "08:00-17:00" }), transmis au contrat. */
  horaires?: Record<string, string> | null;
  lignes: LigneDevis[];
  /** Options de services additionnels sélectionnées (lecture). */
  options?: DevisOption[];
  /** Montant total des options (XAF, chaîne), hors abonnement de base. */
  montant_options?: string;
  /**
   * Temps partiel — heures travaillées par jour (ex. 4.0).
   * Null si temps plein.
   */
  heures_par_jour?: number | null;
  /**
   * Temps partiel — jours travaillés par semaine (ex. 3.0).
   * Null si temps plein.
   */
  jours_par_semaine?: number | null;
  /**
   * Quotité de travail (ex. "0.3000") — lecture seule, calculée par le backend.
   * Null si temps plein.
   */
  quotite?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Contract {
  id: string;
  client: string;
  /** Libellé résolu côté API (source="client.nom_affichage"). */
  client_nom?: string;
  /**
   * Personnel « legacy » du contrat-cadre — peut être null en EN_REVUE (le
   * personnel est désormais affecté via un avenant à la validation).
   */
  personnel: string | null;
  /** Libellé legacy. */
  personnel_nom?: string;
  /** Personnel actuellement affecté via l'avenant actif (source de vérité). */
  personnel_actif_nom?: string;
  /** Avenants d'affectation du contrat (du plus ancien au plus récent). */
  avenants?: Avenant[];
  /** Prix mensuel total facturé = somme des prestations actives (chaîne). */
  prix_total?: string | null;
  devis: string | null;
  type: ContractType;
  date_debut: string;
  date_fin: string | null;
  statut: ContractStatut;
  /** Liste des tâches convenues (strings). */
  taches: string[] | null;
  /** Horaires par jour : ex. { "lundi": "08:00-17:00" }. */
  horaires: Record<string, string> | null;
  salaire_mensuel: string;
  /** Code offre associée (ESSENTIEL|ECO|CONFORT|PREMIUM), facultatif. */
  offre?: CodeOffre | null;
  /** Prix client contractuel en XAF (chaîne), facultatif. */
  prix_unitaire?: string | null;
  signature_client_at: string | null;
  /** Date de signature par l'employeur (Harmaury). Null si non encore signé. */
  signature_employeur_at: string | null;
  /** Vrai si à la fois le client et l'employeur ont signé le contrat-cadre. */
  signe: boolean;
  created_at: string;
  updated_at: string;
}

// ── Entrées (création) ──────────────────────────────────────
export interface CreateLigneDevisInput {
  libelle: string;
  quantite: number;
  prix_unitaire: number;
  montant?: number;
}

/** Entrée d'une option de service dans le payload de création de devis. */
export interface DevisOptionInput {
  /** Code du service additionnel. */
  service: string;
  quantite: number;
}

export interface CreateDevisInput {
  client: string;
  personnel?: string | null;
  type_service: string;
  date_validite: string; // yyyy-MM-dd
  /** Date de début souhaitée (yyyy-MM-dd), optionnelle. */
  date_debut?: string | null;
  /** Tâches convenues pour le futur contrat. */
  taches?: string[] | null;
  /** Horaires par jour (ex. { "lundi": "08:00-17:00" }). */
  horaires?: Record<string, string> | null;
  lignes_input: CreateLigneDevisInput[];
  /** Options de services additionnels à attacher au devis. */
  options_input?: DevisOptionInput[];
  /**
   * Identifiant de l'offre commerciale choisie (ex. 1 pour Essentiel).
   * Transmet la marge de l'offre au contrat généré lors de l'acceptation.
   */
  offre?: string | number | null;
  /**
   * Temps partiel — heures travaillées par jour (ex. 4.0, min 0.5).
   * Doit être fourni avec `jours_par_semaine` pour activer le prorata SMIG.
   */
  heures_par_jour?: number | null;
  /**
   * Temps partiel — jours travaillés par semaine (ex. 3.0, min 0.5).
   * Doit être fourni avec `heures_par_jour` pour activer le prorata SMIG.
   */
  jours_par_semaine?: number | null;
}

// ── Filtres ─────────────────────────────────────────────────
export interface ListDevisParams {
  page?: number;
  page_size?: number;
  statut?: DevisStatut;
  client?: string;
  search?: string;
}

function cleanDevisParams(params: ListDevisParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.statut !== undefined) out.statut = params.statut;
  if (params.client !== undefined && params.client.length > 0) out.client = params.client;
  if (params.search !== undefined && params.search.length > 0) out.search = params.search;
  return out;
}

export async function listDevis(params: ListDevisParams = {}): Promise<Paginated<Devis>> {
  const { data } = await apiClient.get<Paginated<Devis>>('/contracts/devis/', {
    params: cleanDevisParams(params),
  });
  return data;
}

// ── Contrats (route à préfixe vide /contracts/) ─────────────
export interface ListContractsParams {
  page?: number;
  page_size?: number;
  statut?: ContractStatut;
  type?: ContractType;
  client?: string;
  personnel?: string;
}

function cleanContractParams(params: ListContractsParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.statut !== undefined) out.statut = params.statut;
  if (params.type !== undefined) out.type = params.type;
  if (params.client !== undefined && params.client.length > 0) out.client = params.client;
  if (params.personnel !== undefined && params.personnel.length > 0)
    out.personnel = params.personnel;
  return out;
}

/**
 * Liste les contrats (`GET /contracts/` — route DRF à préfixe vide). Le backend
 * filtre par `statut`, `type`, `client`, `personnel`. Sert aux tuiles de synthèse
 * (compteurs via le `count` d'une requête `page_size=1`).
 */
export async function listContracts(
  params: ListContractsParams = {},
): Promise<Paginated<Contract>> {
  const { data } = await apiClient.get<Paginated<Contract>>('/contracts/', {
    params: cleanContractParams(params),
  });
  return data;
}

/** Détail d'un contrat (`GET /contracts/{id}/`) — inclut les `avenants`. */
export async function getContract(contractId: string): Promise<Contract> {
  const { data } = await apiClient.get<Contract>(`/contracts/${contractId}/`);
  return data;
}

export async function createDevis(payload: CreateDevisInput): Promise<Devis> {
  const { data } = await apiClient.post<Devis>('/contracts/devis/', payload);
  return data;
}

/** Envoie un devis BROUILLON → ENVOYÉ (prêt à être accepté). */
export async function envoyerDevis(id: string): Promise<Devis> {
  const { data } = await apiClient.post<Devis>(`/contracts/devis/${id}/envoyer/`);
  return data;
}

/** Accepte un devis ENVOYÉ → génère le contrat (+ paie, CNPS, assurance) atomiquement. */
export async function accepterDevis(id: string): Promise<Contract> {
  const { data } = await apiClient.post<Contract>(`/contracts/devis/${id}/accepter/`);
  return data;
}

// ── Demandes d'employé ──────────────────────────────────────
export type DemandeStatut =
  | 'NOUVELLE'
  | 'EN_TRAITEMENT'
  | 'DEVIS_EMIS'
  | 'REFUSEE'
  | 'ANNULEE';

export const DEMANDE_STATUT_LABELS: Record<DemandeStatut, string> = {
  NOUVELLE: 'Nouvelle',
  EN_TRAITEMENT: 'En traitement',
  DEVIS_EMIS: 'Devis émis',
  REFUSEE: 'Refusée',
  ANNULEE: 'Annulée',
};

/**
 * Format horaires par jour (nouveau format temps partiel).
 * Exemple : { "lundi": { "debut": "08:00", "duree": 6 } }
 */
export interface HorairesJourParJour {
  debut: string;
  duree: number;
}

export interface Demande {
  id: string;
  client: string; // FK-ID résolu via listClients
  metier: string;
  type_service: string;
  statut: DemandeStatut;
  date_souhaitee: string; // ISO yyyy-MM-dd
  /** Tâches standard prédéfinies choisies par le client. */
  taches_standards: string[] | null;
  /** Tâches spécifiques saisies librement par le client. */
  taches_specifiques: string[] | null;
  /** Commentaire libre du client sur le besoin. */
  commentaire: string | null;
  /**
   * Planning horaire. Trois formats possibles :
   * - nouveau (par jour)  : { "lundi": { "debut": "08:00", "duree": 6 }, … }
   * - legacy plage        : { "heure_debut": "08:00", "heure_fin": "17:00", "jours": ["lundi"] }
   * - legacy chaîne       : { "lundi": "08:00-17:00" }
   * Utiliser les type-guards `isHorairesParJour` / `isHorairesLegacyPlage`
   * pour distinguer les formats sans `as any`.
   */
  horaires: Record<string, unknown> | null;
  /**
   * Temps partiel — heures travaillées par jour. Null = temps plein.
   */
  heures_par_jour: number | null;
  /**
   * Temps partiel — jours travaillés par semaine. Null = temps plein.
   */
  jours_par_semaine: number | null;
  created_at: string;
  updated_at: string;
}

export interface ListDemandesParams {
  page?: number;
  page_size?: number;
  statut?: DemandeStatut;
  client?: string;
  search?: string;
}

function cleanDemandeParams(params: ListDemandesParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.statut !== undefined) out.statut = params.statut;
  if (params.client !== undefined && params.client.length > 0) out.client = params.client;
  if (params.search !== undefined && params.search.length > 0) out.search = params.search;
  return out;
}

export async function listDemandes(
  params: ListDemandesParams = {},
): Promise<Paginated<Demande>> {
  const { data } = await apiClient.get<Paginated<Demande>>('/contracts/demandes/', {
    params: cleanDemandeParams(params),
  });
  return data;
}

export interface CreerDevisDepuisDemandeInput {
  lignes: CreateLigneDevisInput[];
  date_validite: string; // yyyy-MM-dd
  /**
   * Personnel pressenti — FACULTATIF. Le devis chiffre un service ; le
   * personnel est affecté par avenant à la validation du contrat.
   */
  personnel?: string | null;
  /**
   * Identifiant de l'offre commerciale choisie (ex. 1 pour Essentiel).
   * Transmet la marge de l'offre au contrat généré lors de l'acceptation.
   */
  offre?: string | number | null;
}

/**
 * Convertit une demande NOUVELLE/EN_TRAITEMENT en devis : crée le Devis associé
 * et bascule la demande au statut DEVIS_EMIS côté serveur.
 * Si `personnel` est fourni, le devis est directement ENVOYE (acceptable par le
 * client) ; sinon il reste en BROUILLON.
 */
export async function creerDevisDepuisDemande(
  id: string,
  payload: CreerDevisDepuisDemandeInput,
): Promise<Devis> {
  const { data } = await apiClient.post<Devis>(
    `/contracts/demandes/${id}/creer_devis/`,
    payload,
  );
  return data;
}

// ── Demandes de services seuls ──────────────────────────────

export type DemandeServiceStatut =
  | 'NOUVELLE'
  | 'EN_TRAITEMENT'
  | 'DEVIS_EMIS'
  | 'CLOTUREE'
  | 'ANNULEE';

export const DEMANDE_SERVICE_STATUT_LABELS: Record<DemandeServiceStatut, string> = {
  NOUVELLE: 'Nouvelle',
  EN_TRAITEMENT: 'En traitement',
  DEVIS_EMIS: 'Devis émis',
  CLOTUREE: 'Clôturée',
  ANNULEE: 'Annulée',
};

export interface DemandeServiceLigne {
  service_code: string;
  service_nom: string;
  service_categorie: string;
  quantite: number;
  prix: string;
  montant: string;
}

export interface DemandeService {
  id: number;
  client: string;
  client_nom: string;
  message: string;
  statut: DemandeServiceStatut;
  devis: number | null;
  lignes: DemandeServiceLigne[];
  montant_estime: string;
  created_at: string;
  updated_at: string;
}

export interface ListDemandesServiceParams {
  page?: number;
  page_size?: number;
  statut?: DemandeServiceStatut;
}

export async function listDemandesService(
  params: ListDemandesServiceParams = {},
): Promise<Paginated<DemandeService>> {
  const query: Record<string, string | number> = {};
  if (params.page !== undefined) query.page = params.page;
  if (params.page_size !== undefined) query.page_size = params.page_size;
  if (params.statut !== undefined) query.statut = params.statut;
  const { data } = await apiClient.get<Paginated<DemandeService>>(
    '/contracts/demandes-service/',
    { params: query },
  );
  return data;
}

export async function updateDemandeService(
  id: number,
  patch: { statut?: string; devis?: number },
): Promise<DemandeService> {
  const { data } = await apiClient.patch<DemandeService>(
    `/contracts/demandes-service/${id}/`,
    patch,
  );
  return data;
}

// ── Salaire & bulletin de paie ───────────────────────────────

export interface DefinirSalaireInput {
  /**
   * Salaire brut mensuel en XAF. Désormais optionnel : si absent, le backend
   * dérive le salaire depuis `prix_unitaire` (ou l'offre) à raison de 80 %
   * du prix client (marge agence = 20 %).
   */
  salaire_mensuel?: string | number;
  annee?: number;
  mois?: number;
  /** Code offre (ESSENTIEL|ECO|CONFORT|PREMIUM). */
  offre?: CodeOffre | null;
  /** Prix client en XAF (chaîne décimale ou nombre). */
  prix_unitaire?: string | number | null;
}

export interface DefinirSalaireResult {
  contract: Contract;
  fiche: FichePaie;
  /** Répartition du prix client (null si l'offre est ESSENTIEL ou inconnue). */
  repartition?: Repartition | null;
  /**
   * Vrai si le salaire dérivé a été planché au SMIG (le prix client est trop bas
   * pour respecter la marge cible de 20 %).
   */
  plancher_smig?: boolean;
}

/**
 * Définit le tarif (offre + prix client) et le salaire d'un contrat, puis
 * génère/rafraîchit la fiche de paie de la période indiquée (mois courant
 * par défaut). Renvoie maintenant `repartition` en plus de `contract`/`fiche`.
 * `POST /contracts/{id}/definir-salaire/`
 * Réservé aux rôles ADMIN et RH.
 */
export async function definirSalaire(
  contractId: string,
  payload: DefinirSalaireInput,
): Promise<DefinirSalaireResult> {
  const { data } = await apiClient.post<DefinirSalaireResult>(
    `/contracts/${contractId}/definir-salaire/`,
    payload,
  );
  return data;
}

// ── Facturation abonnement ───────────────────────────────

export interface FacturerContratInput {
  annee?: number;
  mois?: number;
}

export interface FacturerContratResult {
  facture: Facture;
  /** true → facture créée ; false → facture du mois déjà existante (idempotent). */
  created: boolean;
}

/**
 * Émet la facture d'abonnement du mois courant pour un contrat donné.
 * `POST /contracts/{id}/facturer/`  (ADMIN/RH)
 * - 201 si création, 200 si déjà existante (idempotent).
 * - 400 si le contrat n'a pas de `prix_unitaire`.
 */
export async function facturerContrat(
  contractId: string,
  body: FacturerContratInput = {},
): Promise<FacturerContratResult> {
  const { data } = await apiClient.post<FacturerContratResult>(
    `/contracts/${contractId}/facturer/`,
    body,
  );
  return data;
}

/** Corps de la validation : affectation du personnel (+ salaire + date de début facultatifs). */
export interface ValiderContratInput {
  /** Personnel à affecter — crée l'avenant d'affectation initiale. */
  personnel_id?: string;
  /** Salaire mensuel brut (XAF) ; sinon le brut dérivé du prix est repris. */
  salaire_mensuel?: string;
  /** Date d'effet de l'avenant et du planning (format AAAA-MM-JJ).
   *  Si absent, le backend reprend la date du devis/contrat-cadre. */
  date_debut?: string;
}

/**
 * Valide un contrat EN_REVUE → ACTIF : affecte le personnel (crée l'avenant
 * d'affectation initiale) et génère le bundle (assurance / CNPS / facture /
 * missions / paie). `POST /contracts/{id}/valider/`  (ADMIN/RH)
 */
export async function validerContrat(
  contractId: string,
  body: ValiderContratInput = {},
): Promise<Contract> {
  const { data } = await apiClient.post<Contract>(`/contracts/${contractId}/valider/`, body);
  return data;
}

export interface AjouterPrestationInput {
  personnel_id: string;
  prix_unitaire: string;
  salaire_mensuel?: string;
}

/**
 * Ajoute une prestation supplémentaire (personnel + service + prix) au même
 * contrat-cadre actif — nouvel avenant ACTIF coexistant.
 * `POST /contracts/{id}/ajouter-prestation/` (ADMIN/RH)
 */
export async function ajouterPrestation(
  contractId: string,
  body: AjouterPrestationInput,
): Promise<Contract> {
  const { data } = await apiClient.post<Contract>(
    `/contracts/${contractId}/ajouter-prestation/`,
    body,
  );
  return data;
}

// ── Avenants d'affectation ───────────────────────────────────

/** Liste les avenants d'un contrat (`GET /contracts/avenants/?contract={id}`). */
export async function listAvenants(contractId: string): Promise<Avenant[]> {
  const { data } = await apiClient.get<Paginated<Avenant> | Avenant[]>('/contracts/avenants/', {
    params: { contract: contractId, page_size: 100 },
  });
  return Array.isArray(data) ? data : data.results;
}

/** Signature de l'avenant par le client (`POST /contracts/avenants/{id}/signer-client/`). */
export async function signerAvenantClient(avenantId: string): Promise<Avenant> {
  const { data } = await apiClient.post<Avenant>(
    `/contracts/avenants/${avenantId}/signer-client/`,
  );
  return data;
}

/** Signature de l'avenant par le personnel (`POST .../signer-personnel/`). */
export async function signerAvenantPersonnel(avenantId: string): Promise<Avenant> {
  const { data } = await apiClient.post<Avenant>(
    `/contracts/avenants/${avenantId}/signer-personnel/`,
  );
  return data;
}

/** Télécharge le PDF d'un avenant (auth Bearer via apiClient) et l'ouvre. */
export async function downloadAvenantPdf(avenantId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/contracts/avenants/${avenantId}/pdf/`, {
    responseType: 'blob',
  });
  return data;
}

// ── Signatures employeur (Harmaury) ─────────────────────────

/**
 * Signe le contrat-cadre au nom de l'employeur (Stéphanie MBOUSSI, Responsable RH).
 * `POST /contracts/{id}/signer-employeur/`
 * Body optionnel : `{ image?: string }` (data URL PNG de la signature manuscrite).
 * Réservé aux rôles ADMIN et RH (Staff).
 * Erreur 400 si déjà signé employeur ou si statut EN_REVUE.
 */
export async function signerEmployeurContrat(
  contractId: string,
  image?: string,
): Promise<Contract> {
  const body: { image?: string } = {};
  if (image !== undefined) body.image = image;
  const { data } = await apiClient.post<Contract>(
    `/contracts/${contractId}/signer-employeur/`,
    body,
  );
  return data;
}

/**
 * Signe un avenant au nom de l'employeur.
 * `POST /contracts/avenants/{id}/signer-employeur/`
 * Body optionnel : `{ image?: string }`.
 * Réservé aux rôles ADMIN et RH (Staff).
 * Erreur 400 si déjà signé ou non signable.
 */
export async function signerEmployeurAvenant(
  avenantId: string,
  image?: string,
): Promise<Avenant> {
  const body: { image?: string } = {};
  if (image !== undefined) body.image = image;
  const { data } = await apiClient.post<Avenant>(
    `/contracts/avenants/${avenantId}/signer-employeur/`,
    body,
  );
  return data;
}

// ── Téléchargement PDF contrat-cadre ────────────────────────

/**
 * Récupère le PDF du contrat-cadre comme Blob (auth Bearer via apiClient).
 * `GET /contracts/{id}/pdf/`
 */
export async function downloadContractPdf(contractId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/contracts/${contractId}/pdf/`, {
    responseType: 'blob',
  });
  return data;
}

/** Contrat de travail (Harmaury ↔ personnel) — vue minimale pour l'admin. */
export interface ContratTravailLite {
  id: string;
  reference: string;
  signe: boolean;
  /** Horodatage de signature par le personnel (null si pas encore signé). */
  signature_personnel_at: string | null;
  /** Horodatage de contresignature par Harmaury (employeur). */
  signature_employeur_at: string | null;
}

/**
 * Contresignature du contrat de travail par Harmaury (employeur).
 * `POST /contracts/contrats-travail/{id}/signer-employeur/`
 * Le backend exige que le personnel ait signé au préalable.
 */
export async function signerEmployeurContratTravail(
  id: string,
  image?: string,
): Promise<ContratTravailLite> {
  const body: { image?: string } = {};
  if (image !== undefined) body.image = image;
  const { data } = await apiClient.post<ContratTravailLite>(
    `/contracts/contrats-travail/${id}/signer-employeur/`,
    body,
  );
  return data;
}

/**
 * Contrat de travail d'un personnel (il y en a un par personnel).
 * `GET /contracts/contrats-travail/?personnel={id}` → premier résultat (ou null).
 */
export async function getContratTravailByPersonnel(
  personnelId: string,
): Promise<ContratTravailLite | null> {
  const { data } = await apiClient.get<
    { results?: ContratTravailLite[] } | ContratTravailLite[]
  >('/contracts/contrats-travail/', { params: { personnel: personnelId } });
  const list = Array.isArray(data) ? data : (data.results ?? []);
  return list[0] ?? null;
}

/** PDF du contrat de travail. `GET /contracts/contrats-travail/{id}/pdf/` */
export async function downloadContratTravailPdf(id: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/contracts/contrats-travail/${id}/pdf/`, {
    responseType: 'blob',
  });
  return data;
}
